/**
 * Review an AI estimate before it's logged. Every item is editable (grams
 * rescale its numbers), low-confidence items are flagged, and the assumptions
 * the estimate rests on are shown — a photo estimate is typically within
 * 20–30%, and the person at the table knows things the photo doesn't.
 */

import { useState } from 'react'

import { fmt, fmtKcal, macroColumns, scale, sum, type Nutrients } from '../lib/nutrients'

export type DraftItem = {
  name: string
  grams: number | null
  serving_desc?: string | null
  nutrients: Nutrients
  per100: Nutrients | null
  confidence: 'high' | 'medium' | 'low'
  note?: string | null
  macro_mismatch?: boolean
}

export type Estimate = {
  status: 'ok' | 'no_food' | 'unclear'
  message: string
  items: DraftItem[]
  assumptions: string[]
}

function totalsOf(it: DraftItem & { include: boolean }): Nutrients {
  if (!it.include) return {}
  return it.per100 && it.grams ? scale(it.per100, it.grams) : it.nutrients
}

export function FoodDraft({ estimate, onLog, busy }: {
  estimate: Estimate
  onLog: (items: DraftItem[], groupLabel: string) => void
  busy?: boolean
}) {
  const [items, setItems] = useState(() => estimate.items.map((i) => ({ ...i, include: true })))
  const [label, setLabel] = useState(estimate.message.replace(/^Looks like (a |an )?/i, '').replace(/\.$/, '').slice(0, 60))
  const total = macroColumns(sum(items.map(totalsOf)))

  const set = (i: number, patch: Partial<(typeof items)[number]>) =>
    setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))

  return (
    <div>
      <p style={{ marginBottom: '.3rem' }}>{estimate.message}</p>
      <p className="muted small" style={{ marginTop: 0 }}>
        An estimate — usually within 20–30%. Check the amounts before you log.
      </p>

      {items.map((it, i) => {
        const n = totalsOf(it)
        return (
          <div key={i} className="card" style={{ padding: '.8rem .9rem', marginTop: '.6rem', opacity: it.include ? 1 : 0.5 }}>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <input aria-label="Food name" value={it.name} onChange={(e) => set(i, { name: e.target.value })}
                style={{ fontWeight: 600, padding: '.35rem .5rem' }} />
              <button className="linky" onClick={() => set(i, { include: !it.include })}>
                {it.include ? 'Remove' : 'Add back'}
              </button>
            </div>
            <div className="row small" style={{ marginTop: '.5rem', gap: '.5rem' }}>
              {it.grams !== null ? (
                <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '.35rem', color: 'var(--text)' }}>
                  <input inputMode="decimal" value={it.grams ?? ''} style={{ width: '5.5rem', padding: '.35rem .5rem' }}
                    aria-label={`Grams of ${it.name}`}
                    onChange={(e) => { const g = Number(e.target.value); set(i, { grams: Number.isFinite(g) ? g : 0 }) }} /> g
                </label>
              ) : <span className="muted">{it.serving_desc}</span>}
              <span className="num">{fmtKcal(n.kcal ?? 0)} kcal</span>
              <span className={`tag ${it.confidence === 'low' ? 'low' : ''}`}>{it.confidence}</span>
            </div>
            <div className="muted small" style={{ marginTop: '.3rem' }}>
              P {fmt(n.protein_g ?? 0)} · C {fmt(n.carbs_g ?? 0)} · F {fmt(n.fat_g ?? 0)} g
              {it.serving_desc && it.grams !== null ? ` · ${it.serving_desc}` : ''}
            </div>
            {it.note && <div className="muted small" style={{ marginTop: '.2rem' }}>{it.note}</div>}
            {it.macro_mismatch && <div className="small" style={{ color: 'var(--warn)', marginTop: '.2rem' }}>
              Calories and macros don't quite agree for this item — worth a check.</div>}
          </div>
        )
      })}

      {estimate.assumptions.length > 0 && (
        <details style={{ marginTop: '.8rem' }}>
          <summary className="muted small">Assumptions ({estimate.assumptions.length})</summary>
          <ul className="small muted">{estimate.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </details>
      )}

      <label htmlFor="grp">Meal name</label>
      <input id="grp" value={label} onChange={(e) => setLabel(e.target.value)} />

      <div className="card" style={{ marginTop: '1rem', padding: '.8rem 1rem' }}>
        <div className="row"><b>Total</b><span className="num"><b>{fmtKcal(total.kcal)}</b> kcal</span></div>
        <div className="muted small">P {fmt(total.protein_g)} · C {fmt(total.carbs_g)} · F {fmt(total.fat_g)} g</div>
      </div>

      <button style={{ width: '100%' }} disabled={busy || !items.some((i) => i.include)}
        onClick={() => onLog(items.filter((i) => i.include).map(({ include: _include, ...rest }) => rest), label)}>
        Log {items.filter((i) => i.include).length} item{items.filter((i) => i.include).length === 1 ? '' : 's'}
      </button>
    </div>
  )
}
